import { useState, useCallback } from 'react';
import { toast } from 'sonner';

export interface WatermarkConfig {
  type: 'text' | 'image';
  text?: string;
  imageFile?: File;
  position: 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'custom';
  customX?: number;
  customY?: number;
  opacity: number;
  scale: number;
  rotation: number;
  fontSize?: number;
  fontColor?: string;
  fontFamily?: string;
  tile?: boolean;
}

interface WatermarkedImage {
  id: string;
  original: string;
  watermarked: string;
  blob: Blob;
}

async function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function applyWatermark(
  file: File,
  configs: WatermarkConfig[]
): Promise<Blob> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');
  
  // Draw original image
  ctx.drawImage(img, 0, 0);
  
  // Apply each watermark
  for (const config of configs) {
    ctx.save();
    ctx.globalAlpha = config.opacity;
    
    if (config.type === 'text' && config.text) {
      await applyTextWatermark(ctx, canvas, config);
    } else if (config.type === 'image' && config.imageFile) {
      await applyImageWatermark(ctx, canvas, config);
    }
    
    ctx.restore();
  }
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      'image/png',
      1.0
    );
  });
}

async function applyTextWatermark(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  config: WatermarkConfig
) {
  if (!config.text) return;
  
  const fontSize = config.fontSize || 48;
  const fontFamily = config.fontFamily || 'Arial';
  const color = config.fontColor || '#FFFFFF';
  
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  
  const textMetrics = ctx.measureText(config.text);
  const textWidth = textMetrics.width;
  const textHeight = fontSize;
  
  if (config.tile) {
    const spacing = 100;
    for (let y = 0; y < canvas.height; y += textHeight + spacing) {
      for (let x = 0; x < canvas.width; x += textWidth + spacing) {
        ctx.save();
        ctx.translate(x + textWidth / 2, y + textHeight / 2);
        ctx.rotate((config.rotation * Math.PI) / 180);
        ctx.scale(config.scale, config.scale);
        ctx.fillText(config.text, 0, 0);
        ctx.restore();
      }
    }
  } else {
    const pos = getPosition(canvas, textWidth * config.scale, textHeight * config.scale, config);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate((config.rotation * Math.PI) / 180);
    ctx.scale(config.scale, config.scale);
    ctx.fillText(config.text, 0, 0);
    ctx.restore();
  }
}

async function applyImageWatermark(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  config: WatermarkConfig
) {
  if (!config.imageFile) return;
  
  const watermarkImg = await loadImage(config.imageFile);
  const wmWidth = watermarkImg.naturalWidth * config.scale;
  const wmHeight = watermarkImg.naturalHeight * config.scale;
  
  if (config.tile) {
    const spacing = 50;
    for (let y = 0; y < canvas.height; y += wmHeight + spacing) {
      for (let x = 0; x < canvas.width; x += wmWidth + spacing) {
        ctx.save();
        ctx.translate(x + wmWidth / 2, y + wmHeight / 2);
        ctx.rotate((config.rotation * Math.PI) / 180);
        ctx.drawImage(watermarkImg, -wmWidth / 2, -wmHeight / 2, wmWidth, wmHeight);
        ctx.restore();
      }
    }
  } else {
    const pos = getPosition(canvas, wmWidth, wmHeight, config);
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate((config.rotation * Math.PI) / 180);
    ctx.drawImage(watermarkImg, -wmWidth / 2, -wmHeight / 2, wmWidth, wmHeight);
    ctx.restore();
  }
}

function getPosition(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  config: WatermarkConfig
): { x: number; y: number } {
  switch (config.position) {
    case 'center':
      return { x: canvas.width / 2, y: canvas.height / 2 };
    case 'top-left':
      return { x: width / 2 + 20, y: height / 2 + 20 };
    case 'top-right':
      return { x: canvas.width - width / 2 - 20, y: height / 2 + 20 };
    case 'bottom-left':
      return { x: width / 2 + 20, y: canvas.height - height / 2 - 20 };
    case 'bottom-right':
      return { x: canvas.width - width / 2 - 20, y: canvas.height - height / 2 - 20 };
    case 'custom':
      return { x: config.customX || canvas.width / 2, y: config.customY || canvas.height / 2 };
    default:
      return { x: canvas.width / 2, y: canvas.height / 2 };
  }
}

export function useWatermark() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [watermarkedImages, setWatermarkedImages] = useState<WatermarkedImage[]>([]);

  const addWatermark = useCallback(async (
    file: File,
    configs: WatermarkConfig[]
  ): Promise<WatermarkedImage | null> => {
    try {
      const blob = await applyWatermark(file, configs);
      
      const result: WatermarkedImage = {
        id: crypto.randomUUID(),
        original: URL.createObjectURL(file),
        watermarked: URL.createObjectURL(blob),
        blob,
      };
      
      return result;
    } catch (error) {
      console.error('Error adding watermark:', error);
      toast.error(`Failed to add watermark: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    }
  }, []);

  const processBatch = useCallback(async (
    files: File[],
    configs: WatermarkConfig[]
  ) => {
    setIsProcessing(true);
    setProgress(0);
    setWatermarkedImages([]);
    
    const results: WatermarkedImage[] = [];
    
    for (let i = 0; i < files.length; i++) {
      toast.loading(`Adding watermark to image ${i + 1} of ${files.length}...`, { id: 'watermark-progress' });
      
      const result = await addWatermark(files[i], configs);
      if (result) {
        results.push(result);
        setWatermarkedImages((prev) => [...prev, result]);
      }
      
      setProgress(((i + 1) / files.length) * 100);
    }
    
    toast.success(`Successfully watermarked ${results.length} of ${files.length} images`, { id: 'watermark-progress' });
    setIsProcessing(false);
    
    return results;
  }, [addWatermark]);

  const clearResults = useCallback(() => {
    watermarkedImages.forEach(img => {
      URL.revokeObjectURL(img.original);
      URL.revokeObjectURL(img.watermarked);
    });
    setWatermarkedImages([]);
    setProgress(0);
  }, [watermarkedImages]);

  return {
    isProcessing,
    progress,
    watermarkedImages,
    addWatermark,
    processBatch,
    clearResults,
  };
}
